import React, { useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PartyDetails from './PartyDetails';
import BillItem from './BillItem';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import AdditionalData from './AdditionalData';
import api from '../../utils/axiosConfig';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import './CreateBill.css';

const CreateDemoBill = () => {
    const { billId } = useParams();
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(Boolean(billId));
    const partyDetailsRef = useRef();
    const billItemRef = useRef();
    const additionalDataRef = useRef();

    useEffect(() => {
        if (billId) {
            withLoading(async () => {
                const response = await api.get(`/proformabills/${billId}`);
                const billData = response.data;

                if (partyDetailsRef.current) {
                    const date = new Date(billData.invoiceDate);
                    const adjustedDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
                        .toISOString()
                        .split('T')[0];
                    partyDetailsRef.current.setFormData({
                        invoiceNo: billData.invoiceNo,
                        invoiceDate: adjustedDate,
                        partyName: billData.partyName,
                        partyAddress: billData.partyAddress,
                        siteAddress: billData.siteAddress,
                        workTitle: billData.workTitle,
                        partyGST: billData.partyGst,
                        partyPAN: billData.partyPan,
                        category: billData.category,
                        stateCode: billData.stateCode,
                        state: billData.state,
                        totalAmount: billData.totalAmount,
                        gstRate: billData.gstRate,
                        discount: billData.discount,
                        advance: billData.advance,
                    });
                }

                if (billItemRef.current) {
                    billItemRef.current.setItems(billData.items || []);
                }

                if (additionalDataRef.current) {
                    additionalDataRef.current.setAdditionalData({
                        advance: billData.advance || '',
                        discount: billData.discount || ''
                    });
                }
            }).catch(() => alert('Error fetching Proforma Bill'));
        }
    }, [billId, withLoading]);

    const saveBillData = async () => {
        const partyData = partyDetailsRef.current.getFormData();
        const itemsData = billItemRef.current.getItems();
        const additionalData = additionalDataRef.current.getAdditionalData();

        // Adjust the date for saving
        const date = new Date(partyData.invoiceDate);
        const adjustedDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
            .toISOString()
            .split('T')[0];

        const billData = {
            invoiceDate: adjustedDate,
            partyName: partyData.partyName,
            partyAddress: partyData.partyAddress,
            siteAddress: partyData.siteAddress,
            workTitle: partyData.workTitle,
            partyGst: partyData.partyGST,
            partyPan: partyData.partyPAN,
            category: partyData.category,
            stateCode: partyData.stateCode,
            state: partyData.state,
            totalAmount: itemsData.reduce((sum, item) => sum + parseFloat(item.amount), 0),
            gstRate: partyData.gstRate,
            advance: additionalData.advance || 0,
            discount: additionalData.discount || 0,
            items: itemsData,
        };

        try {
            if (billId) {
                // Update existing bill
                await api.put(`/proformabills/${billId}`, billData);
                alert('Bill updated successfully');
            } else {
                // Create new bill
                await api.post('/proformabills', billData);
                alert('Bill saved successfully');
            }
            return true;
        } catch (error) {
            console.error('Error saving bill:', error);
            alert('Failed to save bill data');
            return false;
        }
    };

    const generatePDF = async() => {
        const doc = new jsPDF();

        // Get page dimensions
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // Function to check and add new page if needed
        const checkAndAddPage = (currentY, requiredSpace) => {
            if (currentY + requiredSpace > pageHeight - 20) { // Leave 20mm margin at bottom
                doc.addPage();
                return 20; // Reset Y to top of new page with margin
            }
            return currentY;
        };


        // Add border around the entire page
        doc.setLineWidth(0.5);
        doc.rect(5, 5, pageWidth - 10, pageHeight - 10); // Add border with 5mm margin from edges

        // Get data from components
        const partyData = partyDetailsRef.current.getFormData();
        const itemsData = billItemRef.current.getItems();
        const additionalData = additionalDataRef.current.getAdditionalData();

        // Add company logo and header
        const img = new Image();
        img.src = process.env.PUBLIC_URL + '/assets/companyLogo.png';

        // Calculate center position for logo
        const logoWidth = 80;  // Width of the logo in mm
        const logoHeight = 35; // Height of the logo in mm
        const xPosition = (pageWidth - logoWidth) / 2;
        // Add centered logo
        doc.addImage(img, 'PNG', xPosition, 7, logoWidth, logoHeight);

        // Add horizontal line after tax invoice header
        doc.setLineWidth(0.5);
        doc.line(15, 42, pageWidth - 15, 42);

        doc.setFontSize(15);
        doc.text('Shivam Buildcare', 15, 50);

        doc.setFontSize(7);
        doc.text('B-531, Money Plant High Street, Opp BSNL Office,', 15, 55);
        doc.text('Godrej Garden City Jagatpur Road, Ahmedabad,', 15, 58);
        doc.text('Ahmedabad, Gujarat, 382470', 15, 61);
        doc.text('GST No - 24JSJPK5641B1ZT', 15, 67);
        doc.text('Pan No - JSJPK5641B', 15, 70);
        doc.text('Udyam No.GJ-01-0371564', 15, 73);

        // Add company header
        doc.setFontSize(15);
        doc.text('Proforma Invoice', pageWidth - 20, 50, { align: 'right' });

        // Add date below Proforma Invoice
        doc.setFontSize(10);
        doc.text(`${formatDateDDMMYYYY(partyData.invoiceDate)}`, pageWidth - 20, 55, { align: 'right' });

        // Add horizontal line after tax invoice header
        doc.setLineWidth(0.5);
        doc.line(15, 78, pageWidth - 15, 78);

        doc.setFontSize(12);
        doc.text('Bill To', 15, 86);

        // Add party details
        doc.setFontSize(7);
        doc.text(`${partyData.partyName}`, 15, 91);
        doc.text(`${partyData.partyAddress}`, 15, 94);
        doc.text(`GST: ${partyData.partyGST}`, 15, 97);
        doc.text(`PAN: ${partyData.partyPAN}`, 15, 100);

        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.text(`Work Title:`, 15, 107);
        doc.text(`Site Address:`, 15, 111);
        doc.setFont(undefined, 'normal');
        doc.text(` ${partyData.workTitle}`, doc.getTextWidth('Work Title:') + 17, 107);
        doc.text(` ${partyData.siteAddress}`, doc.getTextWidth('Site Address:') + 17, 111);

        // Add items table
        // Add items table
        const tableData = itemsData.map((item, index) => [
            index + 1, // Add Item No
            item.description,
            item.unit,
            item.hsn,
            item.quantity,
            item.rate,
            item.amount
        ]);

        autoTable(doc, {
            startY: 115,
            head: [['Sr No', 'Description', 'Unit', 'HSN', 'Quantity', 'Rate', 'Amount']],
            body: tableData,
            styles: {
                fontSize: 8,
                overflow: 'linebreak',
                cellPadding: 2,
                valign: 'top'
            },
            headStyles: {
                fillColor: [34, 34, 34],
                valign: 'middle'
            },
            columnStyles: {
                0: { cellWidth: 12 },
                1: { cellWidth: 70 },
                2: { cellWidth: 18 },
                3: { cellWidth: 20 },
                4: { cellWidth: 20 },
                5: { cellWidth: 20 },
                6: { cellWidth: 22 }
            }
        });

        doc.setFontSize(10);
        // Calculate total
        const total = itemsData.reduce((sum, item) => sum + parseFloat(item.amount), 0);
        const advance = additionalData.advance || 0;
        const discount = additionalData.discount || 0;
        const finalY = doc.lastAutoTable.finalY || 115;
        var theFinalY = finalY + 20;
        const totalAmount = parseFloat(total.toFixed(2));

        // Calculate GST amounts
        const totalGST = parseFloat((partyData.gstRate * 2) / 100); // 0.18
        const cGst = parseFloat((totalGST * 0.5).toFixed(2)); // 0.9
        const sGst = parseFloat((totalGST * 0.5).toFixed(2)); // 0.9

        const gstAmount = parseFloat((totalAmount * totalGST).toFixed(2)); // 0.18 * total bill
        const cGstAmount = parseFloat((totalAmount * cGst).toFixed(2)); // 0.09 * total bill
        const sGstAmount = parseFloat((totalAmount * sGst).toFixed(2)); // 0.09 * total bill
        var finalTotal = parseFloat((totalAmount + gstAmount).toFixed(2)); // final bill including gst
        finalTotal = parseFloat((finalTotal - discount).toFixed(2));
        finalTotal = parseFloat((finalTotal - advance).toFixed(2));

        // After table, check if enough space for totals section
        theFinalY = checkAndAddPage(theFinalY, 45); // 45mm for totals section

        // Add amounts with proper alignment
        const rightMargin = pageWidth - 20;
        const leftMargin = pageWidth - 80;

         // Left side labels and values
        doc.text('Total Amount:', leftMargin, theFinalY);
        doc.text(`${totalAmount}`, rightMargin, theFinalY, { align: 'right' });
        doc.text('CGST 9%:', leftMargin, theFinalY + 5);
        doc.text(`${cGstAmount}`, rightMargin, theFinalY + 5, { align: 'right' });
        doc.text('SGST 9%:', leftMargin, theFinalY + 10);
        doc.text(`${sGstAmount}`, rightMargin, theFinalY + 10, { align: 'right' });
        doc.text('Advance:', leftMargin, theFinalY + 15);
        doc.text(`${advance}`, rightMargin, theFinalY + 15, { align: 'right' });
        doc.text('Discount:', leftMargin, theFinalY + 20);
        doc.text(`${discount}`, rightMargin, theFinalY + 20, { align: 'right' });
        doc.text('Grand Total:', leftMargin, theFinalY + 25);
        doc.text(`${finalTotal}`, rightMargin, theFinalY + 25, { align: 'right' });

        theFinalY += 40;

        // Check space for signatures section
        theFinalY = checkAndAddPage(theFinalY, 30); // 30mm for signatures

        doc.setLineWidth(0.5);
        doc.line(15, theFinalY, pageWidth - 15, theFinalY);

        doc.setFontSize(10);
        doc.text('Received By: ', 15, theFinalY + 7);
        doc.text('Checked By:', 15, theFinalY + 17);

        doc.text('For M/s Shivam Buildcare', pageWidth - 55, theFinalY + 7);

        theFinalY += 22;

        // Check space for bank details section
        theFinalY = checkAndAddPage(theFinalY, 10); // 30mm for bank details

        doc.setLineWidth(0.5);
        doc.line(15, theFinalY, pageWidth - 15, theFinalY);

        theFinalY += 5;

        doc.setFontSize(10);
        doc.setTextColor(255, 0, 0); // Set text color to red
        doc.text('This is just a proforma bill. Please approve the measurement in 7 days and we will provide the actual bill.', 22, theFinalY + 8);
        doc.setTextColor(0, 0, 0); // Reset text color back to black    
        theFinalY += 20;

        doc.setLineWidth(0.5);
        doc.line(15, theFinalY, pageWidth - 15, theFinalY);
        
        // Check space for footer section
        theFinalY = checkAndAddPage(theFinalY, 10); // 35mm for footer

        doc.setFontSize(10);
        doc.text("Shivam Buildcare", pageWidth / 2, theFinalY + 5, { align: 'center' });
        doc.setFontSize(8);
        const addressText = 'B-531 Money Plant High Street Jagatpur Road, Sarkhej-Gandhinagar Hwy,';
        doc.text(addressText, pageWidth / 2, theFinalY + 9, { align: 'center' });
        doc.text('Nr BSNL Office, Gota Ahmedabad Gujarat - 382470', pageWidth / 2, theFinalY + 13, { align: 'center' });

        const iconPhone = new Image();
        iconPhone.src = process.env.PUBLIC_URL + '/assets/iconPhone.png';
        const phoneTextWidth = doc.getTextWidth('+91 99793 63351');
        const phoneIconX = (pageWidth / 2) - (phoneTextWidth / 2) - 4; // 4mm before text start
        doc.addImage(iconPhone, 'PNG', phoneIconX, theFinalY + 16, 2, 2);
        doc.text('+91 99793 63351', pageWidth / 2, theFinalY + 18, { align: 'center' });

        const iconEmail = new Image();
        iconEmail.src = process.env.PUBLIC_URL + '/assets/iconEmail.png';
        const emailTextWidth = doc.getTextWidth('shivambuildcare@gmail.com');
        const emailIconX = (pageWidth / 2) - (emailTextWidth / 2) - 4; // 4mm before text start
        doc.addImage(iconEmail, 'PNG', emailIconX, theFinalY + 20, 2, 2);
        doc.text('shivambuildcare@gmail.com', pageWidth / 2, theFinalY + 22, { align: 'center' });

        // Save the PDF without clearing the form data
        doc.save(`bill_${new Date().getTime()}.pdf`);
    };

    return (
        <PageLoadingArea isLoading={isLoading}>
            <div className="create-bill">
                <div className="create-bill-header">
                    <div>
                        <h1>{billId ? 'Edit proforma invoice' : 'Generate proforma invoice'}</h1>
                        <p className="create-bill-subtitle">
                            {billId
                                ? 'Update client details, line items, and adjustments'
                                : 'Fill in details, add line items, then save or export PDF'}
                        </p>
                    </div>
                    <div className="create-bill-actions">
                        <button
                            type="button"
                            className="create-bill-btn-secondary"
                            onClick={() => navigate('/demoBills')}
                        >
                            Back to list
                        </button>
                    </div>
                </div>

                <PartyDetails ref={partyDetailsRef} showClientSelection={!billId} />
                <BillItem ref={billItemRef} />
                <AdditionalData ref={additionalDataRef} />

                <div className="document-page-footer-actions">
                    <button
                        type="button"
                        className="create-bill-btn-secondary"
                        onClick={generatePDF}
                    >
                        Generate PDF
                    </button>
                    <button
                        type="button"
                        className="create-bill-btn"
                        onClick={saveBillData}
                    >
                        {billId ? 'Update bill' : 'Save bill'}
                    </button>
                </div>
            </div>
        </PageLoadingArea>
    );
};

export default CreateDemoBill;